import './datadog'
import './sentry'
import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { Provider } from 'react-redux'
import { ConfirmProvider } from 'material-ui-confirm'
import {
  StyledEngineProvider,
  ThemeProvider,
  alpha,
  createTheme,
  darken,
  lighten,
} from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { ReactReduxFirebaseProvider } from 'react-redux-firebase'
import { PersistGate } from 'reduxjs-toolkit-persist/integration/react'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/database'
import { registerBuildInfo } from './buildInfo'
import { ensureLatestBuild } from './buildRefresh'
import { persistor, store } from './store'
import { setupClientDevtools } from './devtools/setupClientDevtools'
import App from './features/App/App'
import './index.css'
import { FIREBASE_CONFIG } from './firebase/config'

const theme = createTheme({
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          fontWeight: 700,
          letterSpacing: '0.04em',
          transition:
            'transform 120ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease',
        }),
        contained: ({ theme, ownerState }) => {
          const colorKey =
            ownerState.color && ownerState.color !== 'inherit'
              ? ownerState.color
              : 'primary'
          const paletteColor = theme.palette[colorKey]
          const baseColor = paletteColor.main

          return {
            backgroundColor: baseColor,
            backgroundImage: `linear-gradient(180deg, ${lighten(
              baseColor,
              0.08
            )} 0%, ${baseColor} 58%, ${darken(baseColor, 0.14)} 100%)`,
            border: `1px solid ${alpha(darken(baseColor, 0.38), 0.92)}`,
            boxShadow: `0 1px 0 ${alpha(
              theme.palette.common.white,
              0.18
            )} inset, 0 10px 18px ${alpha(baseColor, 0.18)}`,
            '&:hover': {
              backgroundColor: lighten(baseColor, 0.02),
              backgroundImage: `linear-gradient(180deg, ${lighten(
                baseColor,
                0.12
              )} 0%, ${lighten(baseColor, 0.03)} 58%, ${darken(
                baseColor,
                0.1
              )} 100%)`,
              boxShadow: `0 1px 0 ${alpha(
                theme.palette.common.white,
                0.2
              )} inset, 0 12px 22px ${alpha(baseColor, 0.24)}`,
              transform: 'translateY(-1px)',
            },
            '&:active': {
              boxShadow: `0 1px 0 ${alpha(
                theme.palette.common.white,
                0.14
              )} inset, 0 4px 10px ${alpha(baseColor, 0.2)}`,
              transform: 'translateY(0)',
            },
          }
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
  },
  palette: {
    mode: 'dark',
  },
})

try {
  firebase.initializeApp(FIREBASE_CONFIG)
} catch (err) {
  console.log(err)
}

const rrfProps = {
  firebase,
  config: { userProfile: null }, // Disable automatic user profile syncing
  dispatch: store.dispatch,
}

const Loader: React.FC = () => (
  <Box
    sx={{
      position: 'absolute',
      left: '50%',
      top: '50%',
    }}
  >
    <CircularProgress size={80} thickness={5.6} />
  </Box>
)

Loader.displayName = 'AppLoader'

const AppProviders: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Provider store={store}>
    <ConfirmProvider>
      <PersistGate loading={<Loader />} persistor={persistor}>
        <ReactReduxFirebaseProvider {...rrfProps}>
          <ThemeProvider theme={theme}>
            <StyledEngineProvider injectFirst>{children}</StyledEngineProvider>
          </ThemeProvider>
        </ReactReduxFirebaseProvider>
      </PersistGate>
    </ConfirmProvider>
  </Provider>
)

AppProviders.displayName = 'AppProviders'

registerBuildInfo()

const renderApp = () => {
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <Sentry.ErrorBoundary fallback={<Loader />}>
      <React.StrictMode>
        <AppProviders>
          <App />
        </AppProviders>
      </React.StrictMode>
    </Sentry.ErrorBoundary>
  )
}

const bootstrapApp = async () => {
  const redirectedToLatestBuild = await ensureLatestBuild()

  if (redirectedToLatestBuild) {
    return
  }

  setupClientDevtools({
    firebase,
    persistor,
    store,
  })

  renderApp()
}

void bootstrapApp()
